const createMemoryStorage = () => {
  const store = new Map()
  return {
    getItem(key) {
      return store.has(key) ? store.get(key) : null
    },
    setItem(key, value) {
      store.set(key, value)
    },
    removeItem(key) {
      store.delete(key)
    },
    clear() {
      store.clear()
    },
    key(index) {
      return Array.from(store.keys())[index] ?? null
    },
    get length() {
      return store.size
    },
  }
}

const getGlobalStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage
    }
  } catch (error) {
    console.warn('[secure-ls] Using in-memory storage as fallback:', error)
  }
  return createMemoryStorage()
}

const encode = (value) => {
  const stringified = typeof value === 'string' ? value : JSON.stringify(value)
  if (typeof btoa === 'function') {
    return btoa(unescape(encodeURIComponent(stringified)))
  }
  if (typeof globalThis !== 'undefined' && typeof globalThis.Buffer !== 'undefined') {
    return globalThis.Buffer.from(stringified, 'utf8').toString('base64')
  }
  return stringified
}

const decode = (value) => {
  if (!value) {
    return null
  }
  try {
    const decoded =
      typeof atob === 'function'
        ? atob(value)
        : typeof globalThis !== 'undefined' && typeof globalThis.Buffer !== 'undefined'
          ? globalThis.Buffer.from(value, 'base64').toString('utf8')
          : value
    const normalized = decodeURIComponent(
      decoded
        .split('')
        .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    )
    try {
      return JSON.parse(normalized)
    } catch {
      return normalized
    }
  } catch (error) {
    console.warn('[secure-ls] Failed to decode value:', error)
    return null
  }
}

class SecureLS {
  constructor(options = {}) {
    this._storage = getGlobalStorage()
    this._prefix = options.prefix ?? 'securels:'
    this._encodingType = options.encodingType ?? 'base64'
  }

  get(key) {
    const storedValue = this._storage.getItem(this._prefix + key)
    return decode(storedValue)
  }

  set(key, value) {
    try {
      const encoded = encode(value)
      this._storage.setItem(this._prefix + key, encoded)
    } catch (error) {
      console.warn('[secure-ls] Could not persist value:', error)
    }
  }

  remove(key) {
    this._storage.removeItem(this._prefix + key)
  }

  clear() {
    if (typeof this._storage.clear === 'function') {
      this._storage.clear()
      return
    }
    const keysToRemove = []
    for (let index = 0; index < (this._storage.length ?? 0); index += 1) {
      const storageKey = this._storage.key(index)
      if (storageKey?.startsWith(this._prefix)) {
        keysToRemove.push(storageKey)
      }
    }
    keysToRemove.forEach((storageKey) => this._storage.removeItem(storageKey))
  }

  getAllKeys() {
    const keys = []
    for (let index = 0; index < (this._storage.length ?? 0); index += 1) {
      const storageKey = this._storage.key(index)
      if (storageKey?.startsWith(this._prefix)) {
        keys.push(storageKey.replace(this._prefix, ''))
      }
    }
    return keys
  }
}

SecureLS.default = SecureLS

export default SecureLS
